<template>
  <div class="subscription-wrapper">

    <!-- ================= SECTION ================= -->
    <section class="subscription-section">
      <div class="container">

        <div class="main-heading">
          <h1>Elige tu plan</h1>
          
        </div>

        <div class="plans-grid">

          <div 
            class="plan-card"
            v-for="plan in plans"
            :key="plan.id"
            :class="{ active: isActive && currentPlanName === plan.name }"
          >

            <div 
              v-if="isActive && currentPlanName === plan.name"
              class="badge"
            >
              CURRENT PLAN
            </div>

            <div class="plan-header">
              <h3>{{ plan.name }}</h3>
              <div class="price">
                ${{ plan.price }}
                <span>/ {{ plan.interval }}</span>
              </div>
            </div>

            <ul class="features">
              <li 
                v-for="(feature,index) in parseFeatures(plan.features)" 
                :key="index"
              >
                ✔ {{ feature }}
              </li>
            </ul>

            <div class="plan-footer">
              <button 
                v-if="!(isActive && currentPlanName === plan.name)"
                class="subscribe-btn"
                @click="openCheckout(plan)"
              >
                Subscribe Now
              </button>
            </div>

          </div>

        </div>
      </div>
    </section>


    <!-- ================= CHECKOUT POPUP ================= -->
    <div v-if="showCheckout" class="checkout-overlay">

      <div class="checkout-modal">

        <div class="checkout-header">
          <h3>Complete Your Subscription</h3>
          <span class="close-btn" @click="closeCheckout">✕</span>
        </div>

        <div class="checkout-body" v-if="selectedPlan">

          <div class="plan-summary">
            <h4>{{ selectedPlan.name }}</h4>
            <p class="summary-price">
              ${{ selectedPlan.price }} / {{ selectedPlan.interval }}
            </p>
          </div>

          <div id="paypal-modal-container"></div>

        </div>

      </div>
    </div>


    <!-- ================= PAYMENT RESULT MODAL ================= -->
    <div v-if="showModal" class="modal-overlay">
      <div class="modal-box">

        <div 
          class="modal-header"
          :class="paymentSuccess ? 'success' : 'error'"
        >
          {{ paymentSuccess ? 'Payment Successful 🎉' : 'Payment Failed ❌' }}
        </div>

        <div class="modal-body">
          <p>{{ paymentMessage }}</p>
        </div>

        <div class="modal-footer">
          <button 
            class="modal-btn"
            :class="paymentSuccess ? 'btn-success' : 'btn-error'"
            @click="handleModalClose"
          >
            OK
          </button>
        </div>

      </div>
    </div>

  </div>
</template>

<script>
/* global paypal */
import { HTTP } from '../../_helper/http-constants.js';

export default {
  name: 'SubscriptionPlans',

  data() {
    return {
      plans: [],
      currentPlanName: null,
      isActive: false,

      selectedPlan: null,
      showCheckout: false,

      showModal: false,
      paymentSuccess: false,
      paymentMessage: '',
      redirectAfterClose: false
    }
  },

  methods: {

    /* ================= OPEN CHECKOUT ================= */

    openCheckout(plan) {
      const user = JSON.parse(localStorage.getItem('user'));

      if (!user || !user.email) {
        this.$router.push('/login');
        return;
      }

      this.selectedPlan = plan;
      this.showCheckout = true;

      this.$nextTick(() => {
        const container = document.getElementById('paypal-modal-container');
        if (container) container.innerHTML = '';

        paypal.Buttons({

          style: {
            shape: 'pill',
            color: 'blue',
            layout: 'vertical',
            label: 'subscribe'
          },

          createSubscription: (data, actions) => {
            return actions.subscription.create({
              plan_id: plan.paypal_plan_id
            });
          },

          onApprove: async (data) => {
            await this.saveSubscription(plan.id, data.subscriptionID);
            this.showCheckout = false;
          },

          onError: () => {
            this.showPaymentModal(
              false,
              "Payment failed. Please try again.",
              false
            );
          }

        }).render('#paypal-modal-container');
      });
    },

    closeCheckout() {
      this.showCheckout = false;
    },

    /* ================= PAYMENT RESULT ================= */

    showPaymentModal(success, message, redirect = false) {
      this.paymentSuccess = success;
      this.paymentMessage = message;
      this.redirectAfterClose = redirect;
      this.showModal = true;
    },

    handleModalClose() {
      this.showModal = false;
      if (this.redirectAfterClose) {
        this.$router.push('/home');
      }
    },

    /* ================= API CALLS ================= */

    async fetchPlans() {
      try {
        const res = await HTTP.get('/subscription/plans');
        if(res.data.status){
          this.plans = res.data.data;
        }
      } catch (e) {
        console.error(e);
      }
    },

    async fetchSubscriptionStatus() {
      try {
        const res = await HTTP.get('/subscription/status');
        if (res.data.status && res.data.is_active) {
          this.currentPlanName = res.data.plan_name;
          this.isActive = true;
        }
      } catch (e) {
        console.error(e);
      }
    },

    parseFeatures(features) {
      if (!features) return [];

      let parsed = [];
      try {
        parsed = typeof features === 'string'
          ? JSON.parse(features)
          : features;
      } catch {
        parsed = features.split('\n');
      }

      return parsed.map(f => {
        const div = document.createElement("div");
        div.innerHTML = f;
        return div.textContent || div.innerText || "";
      });
    },

    async saveSubscription(planId, subscriptionId) {
      try {
        const res = await HTTP.post('/subscription/subscribe', {
          plan_id: planId,
          paypal_subscription_id: subscriptionId
        });

        if (res.data.status) {
          this.showPaymentModal(
            true,
            "Your subscription has been activated successfully!",
            true
          );
        }

      } catch (e) {
        this.showPaymentModal(
          false,
          "Something went wrong while activating your subscription.",
          false
        );
      }
    }

  },

  mounted(){
    this.fetchPlans();
    this.fetchSubscriptionStatus();
  }
}
</script>

<style scoped>

/* ================= WRAPPER ================= */

.subscription-wrapper {
  background: #000;
  min-height: 100vh;
  padding: 80px 20px;
  color: #fff;
}

/* ================= HEADING ================= */

.main-heading {
  max-width: 1200px;
  margin: 0 auto 60px auto;
}

.main-heading h1 {
  font-size: 48px;
  font-weight: 700;
}

.main-heading p {
  color: #aaa;
}

/* ================= GRID ================= */

.plans-grid {
  max-width: 1200px;
  margin: auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px;
  align-items: stretch;   /* 👈 ADD */
}

/* ================= CARD ================= */
.plan-card {
  background: #111;
  border-radius: 20px;
  padding: 40px 30px;
  position: relative;
  border: 1px solid #222;
  transition: 0.3s ease;

  display: flex;               /* 👈 ADD */
  flex-direction: column;      /* 👈 ADD */
  justify-content: space-between; /* 👈 ADD */
}
.plan-card:hover {
  transform: translateY(-8px);
}

.plan-card.active {
  border: 2px solid #00cfff;
  box-shadow: 0 0 25px rgba(0, 207, 255, 0.6);
}

.badge {
  position: absolute;
  top: -15px;
  right: 20px;
  background: #00cfff;
  color: #000;
  padding: 6px 15px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

.price {
  font-size: 28px;
  font-weight: 700;
  color: #00cfff;
}

/* ================= BUTTON ================= */

.subscribe-btn {
  width: 100%;
  padding: 14px 20px;     /* 👈 better vertical padding */
  margin-top: 20px;       /* 👈 space from features */
  border-radius: 30px;
  border: none;
  background: linear-gradient(90deg, #00cfff, #007bff);
  color: #fff;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
}

/* ================= CHECKOUT ================= */

.checkout-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.85);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.checkout-modal {
  background: #111;
  width: 95%;
  max-width: 450px;
  border-radius: 20px;
  padding: 30px;
  border: 1px solid #00cfff;
}

.checkout-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
}

.close-btn {
  cursor: pointer;
}

/* ================= RESULT MODAL ================= */

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.8);
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal-box {
  background: #111;
  width: 90%;
  max-width: 400px;
  border-radius: 20px;
  border: 1px solid #00cfff;
}

.modal-header.success {
  background: #00cfff;
  color: #000;
  padding: 15px;
  text-align: center;
}

.modal-header.error {
  background: #ff4d4d;
  color: #fff;
  padding: 15px;
  text-align: center;
}

.modal-body {
  padding: 20px;
  text-align: center;
}

.modal-footer {
  padding: 15px;
  text-align: center;
}

.modal-btn {
  padding: 10px 25px;
  border-radius: 25px;
  border: none;
}
.features {
  list-style: none;
  padding: 0;
  margin: 30px 0 40px 0;   /* 👈 adds space below features */
}

.features li {
  margin-bottom: 14px;     /* 👈 spacing between each feature */
  font-size: 15px;
  color: #ccc;
  display: flex;
  align-items: center;
  gap: 8px;                /* 👈 space between check icon and text */
}
</style>